import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import useDataSourceManager from "hooks/useDataSourceManager";

import RedeemTokenModal from "./components/Modal/RedeemTokenModal";
import PreviewModal from "./components/Modal/PreviewModal";
import EnterModal from "./components/Modal/EnterModal";
import TransferModal from "./components/Modal/TransferModal";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";

import { ReactComponent as AddIcon } from "assets/images/icons/icn_btn_add.svg";
import { ReactComponent as TransferIcon } from "assets/images/icons/icn_btn_connection.svg";
import { ReactComponent as EnterIcon } from "assets/images/icons/icn_chevron_light.svg";

const formatTransferredAt = (value) => {
  const timestamp = normalizeTimestamp(value);

  if (!timestamp) {
    return "-";
  }

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

const chipColor = (text) => {
  switch (text) {
    case "Read-only":
      return {
        bgcolor: "#FFF3E0",
        color: "#F57C00",
      };

    case "Full-control":
      return {
        bgcolor: "#EEF0FF",
        color: "#5062FF",
      };

    case "Resolved":
      return {
        bgcolor: "#EEF0FF",
        color: "#233DCD",
      };

    case "Expired":
      return {
        bgcolor: "#F3F4F6",
        color: "#7A7A7A",
      };

    case "Active":
      return {
        bgcolor: "#E3F5E8",
        color: "#07952B",
      };

    case "Revoked":
      return {
        bgcolor: "#FFE6EF",
        color: "#E5004F",
      };

    case "Transferred":
      return {
        bgcolor: "#EEF0FF",
        color: "#5062FF",
      };

    default:
      return {
        bgcolor: "#F3F4F6",
        color: "#667085",
      };
  }
};

const chipStyle = {
  width: "fit-content",
  height: 22,
  borderRadius: "999px",
  fontSize: 12,
  fontWeight: 500,

  "& .MuiChip-label": {
    px: 1,
  },
};

const TableHeader = ({ columns }) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: columns.map((column) => column.width).join(" "),
        px: 2,
        py: 1.5,
        borderBottom: "1px solid #E5E7EB",
        bgcolor: "#FAFBFD",
      }}
    >
      {columns.map((column, index) => (
        <Typography
          key={`${column.label}-${index}`}
          sx={{
            color: "#667085",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {column.label}
        </Typography>
      ))}
    </Box>
  );
};

const normalizeTimestamp = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === "number") {
    return value < 100000000000 ? value * 1000 : value;
  }

  const stringValue = String(value).trim();

  if (!stringValue) {
    return null;
  }

  if (/^\d+$/.test(stringValue)) {
    const numberValue = Number(stringValue);

    return numberValue < 100000000000 ? numberValue * 1000 : numberValue;
  }

  const parsedValue = Date.parse(stringValue);

  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const formatExpireAt = (expireAt) => {
  const timestamp = normalizeTimestamp(expireAt);

  if (!timestamp) {
    return "-";
  }

  const expireDate = new Date(timestamp);

  if (Number.isNaN(expireDate.getTime())) {
    return "-";
  }

  const dateText = expireDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const remainingDays = Math.ceil(
    (timestamp - Date.now()) / (24 * 60 * 60 * 1000),
  );

  if (remainingDays < 0) {
    return `${dateText} (expired)`;
  }

  if (remainingDays === 0) {
    return `${dateText} (today)`;
  }

  return `${dateText} (in ${remainingDays}d)`;
};

const formatAccessLevel = (accessLevel) => {
  switch (accessLevel) {
    case "read_only":
    case "read-only":
    case "Read-only":
      return "Read-only";

    case "full_control":
    case "full-control":
    case "Full-control":
      return "Full-control";

    default:
      return accessLevel || "-";
  }
};

const formatStatus = (status) => {
  switch (status) {
    case "active":
      return "Active";

    case "resolved":
      return "Resolved";

    case "expired":
      return "Expired";

    case "revoked":
      return "Revoked";

    case "transferred":
      return "Transferred";

    default:
      return status || "-";
  }
};

const formatScopeLabel = (item) => {
  if (item?.scopeLabel) {
    return item.scopeLabel;
  }

  if (item?.scopeType === "org") {
    return item?.orgName ? `Organization: ${item.orgName}` : "Organization";
  }

  if (item?.scopeType === "site") {
    return item?.siteName ? `Site: ${item.siteName}` : "Site";
  }

  return "-";
};

const formatRegion = (item) => {
  const region = item?.regionName ?? item?.regionSummary;

  if (region === null || region === undefined || region === "") {
    return "-";
  }

  return String(region);
};

const Redeem = () => {
  const navigate = useNavigate();

  const { dataMap, getData, sendRequest } = useDataSourceManager();
  const [delegationsUuid] = useState(() => uuidv4());
  const [transferUuid] = useState(() => uuidv4());
  const [transferUpdateUuid] = useState(() => uuidv4());
  const [previewUuid] = useState(() => uuidv4());
  const [redeemUuid] = useState(() => uuidv4());
  const [historyUuid] = useState(() => uuidv4());
  const [delegatedData, setDelegatedData] = useState([]);
  const [delegatedLoading, setDelegatedLoading] = useState(true);
  const [delegatedError, setDelegatedError] = useState("");
  const [transferredData, setTransferredData] = useState([]);
  const [transferredLoading, setTransferredLoading] = useState(true);
  const [transferredError, setTransferredError] = useState("");
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");
  const [openRedeemTokenModal, setOpenRedeemTokenModal] = useState(false);
  const [supportToken, setSupportToken] = useState("");
  const [validateLoading, setValidateLoading] = useState(false);
  const [validateError, setValidateError] = useState("");
  const [tokenStatusData, setTokenStatusData] = useState(null);
  const [openRedeemPreviewModal, setOpenRedeemPreviewModal] = useState(false);
  const [openEnterModal, setOpenEnterModal] = useState(false);
  const [openTransferModal, setOpenTransferModal] = useState(false);
  const [selectedTransferRow, setSelectedTransferRow] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [selectedEnterRow, setSelectedEnterRow] = useState(null);
  const validatingTokenRef = useRef(null);

  const getDelegations = () => {
    setDelegatedLoading(true);
    setDelegatedError("");

    sendRequest("/me/supportTokens/delegations.read", delegationsUuid, {});
  };

  const getTransferred = () => {
    setTransferredLoading(true);
    setTransferredError("");

    sendRequest("/me/supportTokens/transferred.read", transferUuid, {});
  };

  const getHistory = () => {
    setHistoryLoading(true);
    setHistoryError("");

    sendRequest("/me/supportTokens/history.read", historyUuid, {});
  };

  useEffect(() => {
    getDelegations();
    getTransferred();
    getHistory();
  }, []);

  useEffect(() => {
    const response = getData(delegationsUuid);

    if (!response) {
      return;
    }

    if (response?.error) {
      console.error("Get delegations failed:", response.error);

      setDelegatedData([]);
      setDelegatedError(
        response.error?.message || "Failed to get delegated access.",
      );
      setDelegatedLoading(false);

      return;
    }

    const responseData =
      response?.result?.data ??
      response?.data?.result?.data ??
      response?.data ??
      response?.result ??
      null;

    const items = Array.isArray(responseData)
      ? responseData
      : (responseData?.items ??
        responseData?.delegations ??
        responseData?.list ??
        []);

    const normalizedData = items.map((item, index) => ({
      id:
        item?.supportTokenId ??
        item?.delegationId ??
        item?.id ??
        `delegation-${index}`,
      supportTokenId: item?.supportTokenId ?? item?.id ?? null,
      customer: formatScopeLabel(item),
      tag: "Delegated · support token",
      sharedFrom: item?.sharedFrom,
      region: formatRegion(item),
      access: formatAccessLevel(item?.accessLevel),
      status: formatStatus(item?.status),
      expires: formatExpireAt(item?.expireAt),
      scopeType: item?.scopeType ?? null,
      orgId: item?.orgId ?? null,
      siteId: item?.siteId ?? null,
      originalData: item,
    }));

    setDelegatedData(normalizedData);
    setDelegatedError("");
    setDelegatedLoading(false);
  }, [dataMap, getData, delegationsUuid]);

  useEffect(() => {
    const response = getData(transferUuid);

    if (!response) {
      return;
    }

    if (response?.error) {
      console.error("Get transferred support tokens failed:", response.error);

      setTransferredData([]);
      setTransferredError(
        response.error?.message || "Failed to get transferred support tokens.",
      );
      setTransferredLoading(false);

      return;
    }

    const responseData =
      response?.result?.data ??
      response?.data?.result?.data ??
      response?.data ??
      response?.result ??
      null;

    const items = Array.isArray(responseData)
      ? responseData
      : (responseData?.items ?? []);

    const normalizedData = items.map((item, index) => ({
      id: item?.supportTokenId ?? `transferred-${index}`,

      supportTokenId: item?.supportTokenId ?? null,

      customer: item?.scopeLabel ?? "-",

      owner:
        item?.toSupportDisplayName && item?.toSupportEmail
          ? `${item.toSupportDisplayName} (${item.toSupportEmail})`
          : (item?.toSupportDisplayName ?? item?.toSupportEmail ?? "-"),

      toSupportUserId: item?.toSupportUserId ?? null,

      time: formatTransferredAt(item?.transferredAt),

      originalData: item,
    }));

    setTransferredData(normalizedData);
    setTransferredError("");
    setTransferredLoading(false);
  }, [dataMap, getData, transferUuid]);

  useEffect(() => {
    const response = getData(historyUuid);

    if (!response) {
      return;
    }

    if (response?.error) {
      console.error("Get history failed:", response.error);

      setHistoryData([]);
      setHistoryError(response.error?.message || "Failed to get history.");
      setHistoryLoading(false);

      return;
    }

    const responseData =
      response?.result?.data ??
      response?.data?.result?.data ??
      response?.data ??
      response?.result ??
      null;

    const items = Array.isArray(responseData)
      ? responseData
      : (responseData?.items ?? []);

    const normalizedData = items.map((item, index) => ({
      id: item?.supportTokenId ?? `history-${index}`,

      customer: item?.scopeLabel ?? "-",

      access: formatAccessLevel(item?.accessLevel),

      status: formatStatus(item?.status),

      ended: formatTransferredAt(item?.endedAt),

      originalData: item,
    }));

    setHistoryData(normalizedData);
    setHistoryError("");
    setHistoryLoading(false);
  }, [dataMap, getData, historyUuid]);

  const handleOpenRedeemTokenModal = () => {
    setSupportToken("");
    setValidateError("");
    setPreviewData(null);

    validatingTokenRef.current = null;

    setOpenRedeemPreviewModal(false);
    setOpenRedeemTokenModal(true);
  };

  const handleCloseRedeemPreviewModal = () => {
    if (redeemLoading) {
      return;
    }

    setOpenRedeemPreviewModal(false);
    setPreviewData(null);
    setSupportToken("");
  };

  const handleCloseRedeemTokenModal = () => {
    if (validateLoading) {
      return;
    }

    setOpenRedeemTokenModal(false);
    setSupportToken("");
    setValidateError("");
    setTokenStatusData(null);

    validatingTokenRef.current = null;
  };

  const handleTokenChange = (value) => {
    setSupportToken(value);

    if (validateError) {
      setValidateError("");
    }
  };

  const handleValidateToken = () => {
    const supportTokenId = supportToken.trim();

    if (!supportTokenId || validateLoading) {
      return;
    }

    setValidateLoading(true);
    setValidateError("");
    setTokenStatusData(null);

    validatingTokenRef.current = supportTokenId;

    sendRequest(
      "/me/supportTokens/preview.read",
      previewUuid,
      {},
      {
        data: {
          supportTokenPlainTextId: supportTokenId,
        },
      },
    );
  };

  useEffect(() => {
    if (!validateLoading || !validatingTokenRef.current) {
      return;
    }

    const response = getData(previewUuid);

    if (!response) {
      return;
    }

    if (response?.error) {
      console.error("Get support token preview failed:", response.error);

      setValidateError(
        response.error?.message || "Failed to validate the support token.",
      );

      setPreviewData(null);
      setValidateLoading(false);

      validatingTokenRef.current = null;

      return;
    }

    const responseData =
      response?.result?.data ??
      response?.data?.result?.data ??
      response?.data ??
      response?.result ??
      null;

    if (!responseData) {
      setValidateError("The API response did not contain preview data.");

      setPreviewData(null);
      setValidateLoading(false);

      validatingTokenRef.current = null;

      return;
    }

    setPreviewData({
      ...responseData,
      supportTokenPlainTextId: validatingTokenRef.current,
    });

    setValidateError("");
    setValidateLoading(false);

    validatingTokenRef.current = null;

    setOpenRedeemTokenModal(false);
    setOpenRedeemPreviewModal(true);
  }, [dataMap, getData, previewUuid, validateLoading]);

  const handleEnter = (row) => {
    setSelectedEnterRow(row);
    setOpenEnterModal(true);
  };

  const handleCloseEnterModal = () => {
    setOpenEnterModal(false);
    setSelectedEnterRow(null);
  };

  const handleConfirmEnter = () => {
    if (!selectedEnterRow) return;

    const originalData = selectedEnterRow.originalData || {};

    const scopeType =
      selectedEnterRow.scopeType ??
      originalData.scopeType ??
      (originalData.siteId ? "site" : "org");

    const orgId =
      selectedEnterRow.orgId ??
      originalData.orgId ??
      null;

    const siteId =
      selectedEnterRow.siteId ??
      originalData.siteId ??
      null;

    const orgName =
      originalData.orgName ?? "";

    const siteName =
      originalData.siteName ??
      selectedEnterRow.customer?.replace(/^Site:\s*/i, "") ??
      "";

    const supportSession = {
      supportTokenId:
        selectedEnterRow.supportTokenId ??
        originalData.supportTokenId ??
        null,

      scopeType,

      orgId,
      orgName,

      siteId,
      siteName,

      scopeLabel:
        originalData.scopeLabel ??
        selectedEnterRow.customer ??
        "",

      accessLevel:
        originalData.accessLevel ??
        selectedEnterRow.access,

      sharedFrom:
        originalData.sharedFrom ??
        selectedEnterRow.sharedFrom,

      expireAt: originalData.expireAt,
      expiresText: selectedEnterRow.expires,
    };

    sessionStorage.setItem(
      "supportSession",
      JSON.stringify(supportSession),
    );

    const currentBread = JSON.parse(
      localStorage.getItem("selectBreadcrumbs") || "{}",
    );

    if (scopeType === "org") {
      currentBread.site = {};

      // delegated org
      currentBread.orgs = {
        ...(currentBread.orgs || {}),
        id: orgId,
        name: orgName,
      };

      localStorage.setItem(
        "selectBreadcrumbs",
        JSON.stringify(currentBread),
      );

      setOpenEnterModal(false);
      setSelectedEnterRow(null);

      navigate("/org/sites", {
        replace: true,
      });

      return;
    }

    if (scopeType === "site") {

      currentBread.orgs = {
        ...(currentBread.orgs || {}),
        id: orgId,
        name: orgName,
      };

      currentBread.site = {
        id: siteId,
        name: siteName,
      };

      localStorage.setItem(
        "selectBreadcrumbs",
        JSON.stringify(currentBread),
      );

      setOpenEnterModal(false);
      setSelectedEnterRow(null);

      navigate("/site/dashboard", {
        replace: true,
      });
    }
  };
  const handleTransfer = (row) => {
    setSelectedTransferRow(row);
    setTransferError("");
    setOpenTransferModal(true);
  };

  const handleCloseTransferModal = () => {
    if (transferLoading) {
      return;
    }
    setOpenTransferModal(false);
    setSelectedTransferRow(null);
    setTransferError("");
  };

  const handleConfirmTransfer = ({ row, email }) => {
    const supportTokenId =
      row?.supportTokenId ?? row?.originalData?.supportTokenId ?? row?.id;

    const targetEmail = email?.trim();

    if (!supportTokenId) {
      setTransferError("Support token ID is missing.");
      return;
    }

    if (!targetEmail) {
      setTransferError("Target support account email is required.");
      return;
    }

    if (transferLoading) {
      return;
    }

    setTransferLoading(true);
    setTransferError("");

    sendRequest(
      "/me/supportTokens/:supportTokenId/transfer.update",
      transferUpdateUuid,
      {
        supportTokenId,
      },
      {
        updates: {
          targetEmail,
        },
      },
    );
  };

  useEffect(() => {
    if (!transferLoading) {
      return;
    }

    const response = getData(transferUpdateUuid);

    if (!response) {
      return;
    }

    if (response?.error) {
      console.error("Transfer support token failed:", response.error);

      setTransferError(
        response.error?.message || "Failed to transfer delegated scope.",
      );

      setTransferLoading(false);
      return;
    }

    setTransferLoading(false);
    setOpenTransferModal(false);
    setSelectedTransferRow(null);
    setTransferError("");

    getDelegations();
    getTransferred();
  }, [dataMap, getData, transferUpdateUuid, transferLoading]);

  const handleRedeemToken = () => {
    const supportTokenId =
      previewData?.supportTokenId ??
      previewData?.supportTokenPlainTextId ??
      supportToken.trim();

    if (!supportTokenId || redeemLoading) {
      return;
    }

    setRedeemLoading(true);

    sendRequest(
      "/me/supportTokens/:supportTokenId/status.update",
      redeemUuid,
      {
        supportTokenId,
      },
      {
        updates: {
          status: "active",
        },
      },
    );
  };

  useEffect(() => {
    if (!redeemLoading) {
      return;
    }

    const response = getData(redeemUuid);

    if (!response) {
      return;
    }

    if (response?.error) {
      console.error("Redeem token failed:", response.error);

      setRedeemLoading(false);
      return;
    }

    setRedeemLoading(false);
    setOpenRedeemPreviewModal(false);
    setPreviewData(null);
    setSupportToken("");

    getDelegations();
  }, [dataMap, getData, redeemUuid, redeemLoading]);

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        bgcolor: "#F8F9FC",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        mb={3}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" fontWeight={700} mb={1}>
            Redeem & delegated access
          </Typography>

          <Typography
            sx={{
              maxWidth: 1100,
              color: "#667085",
              fontSize: 14,
            }}
          >
            Access every org/site delegated to you, with who shared each one.
            Delegated scopes are kept here — your Sites view stays your own
            environment only. A scope a colleague transfers to you appears under
            Delegated to me automatically — no code needed.
          </Typography>
        </Box>

        <SolidButton
          startIcon={<AddIcon />}
          width="160px"
          height="32px"
          onClick={handleOpenRedeemTokenModal}
        >
          Redeem a token
        </SolidButton>
      </Stack>

      <Typography fontWeight={700} fontSize={26} mb={2}>
        Delegated to me
      </Typography>

      {delegatedLoading ? (
        <Box
          sx={{
            minHeight: "100px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : delegatedError ? (
        <Box
          sx={{
            minHeight: "100px",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: 1,
            mb: 5,
          }}
        >
          <Typography
            sx={{
              color: "#D92D20",
              fontSize: 14,
            }}
          >
            {delegatedError}
          </Typography>

          <Button
            onClick={getDelegations}
            sx={{
              px: 0,
              minWidth: 0,
              color: "#16296C",
              textTransform: "none",
            }}
          >
            Retry
          </Button>
        </Box>
      ) : delegatedData.length === 0 ? (
        <Typography
          sx={{
            color: "#667085",
            fontSize: 16,
            mb: 5,
          }}
        >
          No org/site is delegated to you right now. Redeem a token above, or
          wait for a colleague to transfer one to you.
        </Typography>
      ) : (
        <Paper
          sx={{
            width: "100%",
            overflowX: "auto",
            borderRadius: 3,
          }}
        >
          <Box sx={{ minWidth: "1120px" }}>
            <TableHeader
              columns={[
                {
                  label: "Customer scope",
                  width: "2fr",
                },
                {
                  label: "Shared from",
                  width: "2.5fr",
                },
                {
                  label: "Region",
                  width: "1fr",
                },
                {
                  label: "Access",
                  width: "1fr",
                },
                {
                  label: "Expires",
                  width: "1.2fr",
                },
                {
                  label: "",
                  width: "1.4fr",
                },
              ]}
            />

            {delegatedData.map((row, index) => (
              <Box
                key={`${row.id}-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 2.5fr 1fr 1fr 1.2fr 1.4fr",
                  alignItems: "center",
                  px: 2,
                  py: 2,
                  borderBottom: "1px solid #ECECEC",

                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    title={row.customer}
                    fontWeight={600}
                    sx={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.customer}
                  </Typography>

                  <Chip
                    label={row.tag}
                    size="small"
                    sx={{
                      mt: 1,
                      width: "fit-content",
                      height: 22,
                      bgcolor: "#EEF2FF",
                      color: "#5163FF",
                      fontSize: 12,

                      "& .MuiChip-label": {
                        px: 1,
                      },
                    }}
                  />
                </Box>

                <Typography
                  title={row.sharedFrom}
                  sx={{
                    minWidth: 0,
                    pr: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.sharedFrom}
                </Typography>

                <Typography>{row.region}</Typography>

                <Chip
                  label={row.access}
                  size="small"
                  sx={{
                    ...chipStyle,
                    ...chipColor(row.access),
                  }}
                />

                <Typography
                  sx={{
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.expires}
                </Typography>

                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <SolidButton
                    disabled={row.status !== "Active"}
                    onClick={() => handleEnter(row)}
                    iconEnd={
                      <EnterIcon
                        style={{
                          width: "14px",
                          height: "14px",
                        }}
                      />
                    }
                    width="100px"
                    height="32px"
                  >
                    Enter
                  </SolidButton>

                  <OutlineButton
                    width="100px"
                    height="32px"
                    onClick={() => handleTransfer(row)}
                    disabled={row.status !== "Active"}
                    startIcon={<TransferIcon />}
                  >
                    Transfer
                  </OutlineButton>
                </Stack>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
      <Typography fontWeight={700} fontSize={26} mt={5} mb={2}>
        Transferred to another engineer
      </Typography>

      {transferredLoading ? (
        <Box
          sx={{
            minHeight: "100px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : transferredError ? (
        <Box
          sx={{
            minHeight: "100px",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: 1,
            mb: 5,
          }}
        >
          <Typography
            sx={{
              color: "#D92D20",
              fontSize: 14,
            }}
          >
            {transferredError}
          </Typography>

          <Button
            onClick={getTransferred}
            sx={{
              px: 0,
              minWidth: 0,
              color: "#16296C",
              textTransform: "none",
            }}
          >
            Retry
          </Button>
        </Box>
      ) : transferredData.length === 0 ? (
        <Typography
          sx={{
            color: "#667085",
            fontSize: 16,
            mb: 5,
          }}
        >
          You haven't transferred any sessions.
        </Typography>
      ) : (
        <Paper
          sx={{
            width: "100%",
            overflowX: "auto",
            borderRadius: 3,
          }}
        >
          <Box sx={{ minWidth: "760px" }}>
            <TableHeader
              columns={[
                {
                  label: "Customer scope",
                  width: "2fr",
                },
                {
                  label: "Now responsible",
                  width: "3fr",
                },
                {
                  label: "Transferred on",
                  width: "2fr",
                },
              ]}
            />

            {transferredData.map((row, index) => (
              <Box
                key={`${row.id}-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 3fr 2fr",
                  alignItems: "center",
                  px: 2,
                  py: 2,
                  borderBottom: "1px solid #ECECEC",

                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                <Typography
                  title={row.customer}
                  fontWeight={600}
                  sx={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.customer}
                </Typography>

                <Typography
                  title={row.owner}
                  sx={{
                    minWidth: 0,
                    pr: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.owner}
                </Typography>

                <Typography
                  sx={{
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.time}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <Typography fontWeight={700} fontSize={26} mt={5} mb={2}>
        Recently handled
      </Typography>

      {historyLoading ? (
        <Box
          sx={{
            minHeight: "100px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : historyError ? (
        <Typography
          sx={{
            color: "#D92D20",
            fontSize: 14,
            mb: 5,
          }}
        >
          {historyError}
        </Typography>
      ) : historyData.length === 0 ? (
        <Typography
          sx={{
            color: "#667085",
            fontSize: 16,
            mb: 5,
          }}
        >
          No past sessions handled by you yet.
        </Typography>
      ) : (
        <Paper
          sx={{
            width: "100%",
            overflowX: "auto",
            borderRadius: 3,
          }}
        >
          <Box sx={{ minWidth: "760px" }}>
            <TableHeader
              columns={[
                {
                  label: "Customer scope",
                  width: "2fr",
                },
                {
                  label: "Access",
                  width: "1.5fr",
                },
                {
                  label: "Status",
                  width: "1.5fr",
                },
                {
                  label: "Ended on",
                  width: "2fr",
                },
              ]}
            />

            {historyData.map((row, index) => (
              <Box
                key={`${row.id}-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.5fr 1.5fr 2fr",
                  alignItems: "center",
                  px: 2,
                  py: 2,
                  borderBottom: "1px solid #ECECEC",

                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                <Typography>{row.customer}</Typography>

                <Chip
                  label={row.access}
                  size="small"
                  sx={{
                    ...chipStyle,
                    ...chipColor(row.access),
                  }}
                />

                <Chip
                  label={row.status}
                  size="small"
                  sx={{
                    ...chipStyle,
                    ...chipColor(row.status),
                  }}
                />

                <Typography>{row.ended}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <RedeemTokenModal
        open={openRedeemTokenModal}
        token={supportToken}
        loading={validateLoading}
        errorMessage={validateError}
        onTokenChange={handleTokenChange}
        onClose={handleCloseRedeemTokenModal}
        onValidate={handleValidateToken}
      />

      <PreviewModal
        open={openRedeemPreviewModal}
        previewData={previewData}
        loading={redeemLoading}
        onClose={handleCloseRedeemPreviewModal}
        onRedeem={handleRedeemToken}
      />

      <EnterModal
        open={openEnterModal}
        row={selectedEnterRow}
        onClose={handleCloseEnterModal}
        onConfirm={handleConfirmEnter}
      />

      <TransferModal
        open={openTransferModal}
        row={selectedTransferRow}
        loading={transferLoading}
        errorMessage={transferError}
        onClose={handleCloseTransferModal}
        onConfirm={handleConfirmTransfer}
      />
    </Box>
  );
};

export default Redeem;
